import React, { memo } from 'react';
import { Plus, Menu, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../utils';
import type { User } from '../../types/auth';
import './Header.css';

interface HeaderProps {
  className?: string;
  user?: User | null;
  onNewChat?: () => void;
  onOpenSidebar?: () => void;
  onLogout?: () => void;
}

const HeaderComponent: React.FC<HeaderProps> = ({
  className,
  user,
  onNewChat,
  onOpenSidebar,
  onLogout,
}) => {
  return (
    <header className={cn('header', className)}>
      <div className="header-content">
        <div className="header-left">
          {onOpenSidebar && (
            <button
              type="button"
              className="header-button header-button--menu"
              onClick={onOpenSidebar}
              aria-label="Открыть историю чатов"
            >
              <Menu size={22} />
            </button>
          )}
          <div className="logo">
            <img 
              src="https://okan.ru/wp-content/uploads/2021/09/logo-1.svg" 
              alt="Logo" 
              className="logo-icon"
            />
          </div>
        </div>
        
        <div className="header-right">
          {user && (
            <span className="header-user" title={user.displayName || user.login}>
              {user.displayName || user.login}
            </span>
          )}
          {onLogout && (
            <button
              type="button"
              className="header-button header-button--logout"
              onClick={onLogout}
              aria-label="Выйти"
              title="Выйти"
            >
              <LogOut size={20} />
            </button>
          )}
          {onNewChat && (
            <motion.button
              className="header-button header-button--new-chat"
              onClick={onNewChat}
              aria-label="Start new chat"
              initial={{ scale: 1 }}
              animate={{
                background: [
                  "linear-gradient(90deg, #667eea 0%, #764ba2 20%, #f093fb 40%, #f5576c 60%, #4facfe 80%, #667eea 100%)",
                  "linear-gradient(90deg, #764ba2 0%, #f093fb 20%, #f5576c 40%, #4facfe 60%, #667eea 80%, #764ba2 100%)",
                  "linear-gradient(90deg, #f093fb 0%, #f5576c 20%, #4facfe 40%, #667eea 60%, #764ba2 80%, #f093fb 100%)",
                  "linear-gradient(90deg, #f5576c 0%, #4facfe 20%, #667eea 40%, #764ba2 60%, #f093fb 80%, #f5576c 100%)",
                  "linear-gradient(90deg, #4facfe 0%, #667eea 20%, #764ba2 40%, #f093fb 60%, #f5576c 80%, #4facfe 100%)",
                  "linear-gradient(90deg, #667eea 0%, #764ba2 20%, #f093fb 40%, #f5576c 60%, #4facfe 80%, #667eea 100%)"
                ],
                boxShadow: [
                  "0 4px 15px rgba(0, 0, 0, 0.2)",
                  "0 6px 25px rgba(102, 126, 234, 0.4)",
                  "0 4px 15px rgba(0, 0, 0, 0.2)"
                ]
              }}
              transition={{
                background: {
                  duration: 6,
                  repeat: Infinity,
                  ease: "linear"
                },
                boxShadow: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
              whileHover={{
                scale: 1.05,
                background: [
                  "linear-gradient(90deg, #5a67d8 0%, #6b46c1 20%, #ec4899 40%, #dc2626 60%, #2563eb 80%, #5a67d8 100%)",
                  "linear-gradient(90deg, #6b46c1 0%, #ec4899 20%, #dc2626 40%, #2563eb 60%, #5a67d8 80%, #6b46c1 100%)",
                  "linear-gradient(90deg, #ec4899 0%, #dc2626 20%, #2563eb 40%, #5a67d8 60%, #6b46c1 80%, #ec4899 100%)",
                  "linear-gradient(90deg, #dc2626 0%, #2563eb 20%, #5a67d8 40%, #6b46c1 60%, #ec4899 80%, #dc2626 100%)",
                  "linear-gradient(90deg, #2563eb 0%, #5a67d8 20%, #6b46c1 40%, #ec4899 60%, #dc2626 80%, #2563eb 100%)",
                  "linear-gradient(90deg, #5a67d8 0%, #6b46c1 20%, #ec4899 40%, #dc2626 60%, #2563eb 80%, #5a67d8 100%)"
                ],
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)",
                transition: {
                  background: {
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear"
                  }
                }
              }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Новый чат</span>
              <Plus size={20} />
            </motion.button>
          )}
        </div>
      </div>
    </header>
  );
};

export const Header = memo(HeaderComponent);
